import { z } from "zod";
import type { Customer, Order, Promotion } from "../types";
import { audit, fail, getSettings, newId, parse, requirePermission, type Actor, type Ctx } from "./context";
import { OnlineChannelSchema, ONLINE_CHANNEL_LABELS, type OnlineChannel } from "../core";
import { OrderLineInputSchema, createTicket, moveStock, onlineCounterId, paymentStateOf, placeOrder, quoteOrder, reprice, settle } from "./orders";

/**
 * Orders placed by customers in the ordering app. The app is anonymous, so the trusted backend
 * runs these commands as this fixed actor: it can create an order and nothing else. Everything a
 * customer sends is reduced to "which dishes, how many, for whom" - prices, discounts, complimentary
 * items and payments can never come from the app.
 */
export const ONLINE_ACTOR: Actor = { id: "customer-app", name: "Customer app", role: "waiter", permissions: [] };
/** The same actor when the payment gateway confirms a card payment. */
export const ONLINE_PAYMENT_ACTOR: Actor = { ...ONLINE_ACTOR, permissions: ["payments.capture"] };

/** Unpaid app orders one phone number may have waiting at a time, so a prank cannot flood the kitchen. */
export const MAX_OPEN_ONLINE_ORDERS = 2;
/** Shift id recorded on a gateway payment that arrives while no cash shift is open. It never touches a drawer. */
export const NO_SHIFT = "online";

/** How long after placing an order the customer can still cancel it in the app. After that, they call the restaurant. */
export const CUSTOMER_CANCEL_SECONDS = 30;
export const CUSTOMER_CANCEL_REASON = "Cancelled by the customer";

const OPEN_SHIFT = [{ field: "status", op: "==" as const, value: "open" }];
/**
 * Starts the reads a command is known to need all at once. On the hosted store every read is a
 * round trip that is then remembered, so the engine's one-by-one reads afterwards cost nothing:
 * an order that took a dozen round trips in a row takes about one.
 */
const warm = (ctx: Ctx, menuItemIds: readonly string[], more: Promise<unknown>[] = [], channel: OnlineChannel = "app") => Promise.all([
  ctx.tx.get("settings", "restaurant"), ctx.tx.query("shifts", OPEN_SHIFT), ctx.tx.query("promotions", [{ field: "active", op: "==", value: true }]),
  ctx.tx.get("orderCounters", onlineCounterId(ctx.businessDate, channel)), ctx.tx.get("orderCounters", "auditChain"),
  ...[...new Set(menuItemIds)].map((id) => ctx.tx.get("menuItems", id)), ...more
]);

const OnlineLineSchema = OrderLineInputSchema.omit({ complimentary: true }).extend({ quantity: z.number().int().min(1).max(20) });
export const OnlineOrderInputSchema = z.object({
  idempotencyKey: z.string().uuid(),
  customerName: z.string().trim().min(2, "Enter your name").max(60),
  // Ten digits, stored the way the cashier types them (0771234567) so the customer record is shared.
  customerPhone: z.string().transform((value) => value.replace(/[\s-]/g, "").replace(/^(?:\+94|0094|94)(?=\d{9}$)/, "0")).pipe(z.string().regex(/^0\d{9}$/, "Enter your 10-digit phone number, for example 0771234567")),
  lines: z.array(OnlineLineSchema).min(1).max(30), note: z.string().trim().max(200).default(""),
  /** Set by the server from the request, never taken from what the customer typed. Absent from older callers: the app. */
  channel: OnlineChannelSchema.default("app")
});

async function requireOpenForOrders(ctx: Ctx): Promise<void> {
  const settings = await getSettings(ctx.tx);
  if (settings.onlineOrdersEnabled === false) fail("failed-precondition", "Online ordering is paused right now. Please call the restaurant.");
  // An open cash shift means staff are at the counter to see the order and take the payment.
  const shift = (await ctx.tx.query("shifts", OPEN_SHIFT))[0];
  if (!shift) fail("failed-precondition", "The restaurant is closed right now. Please order when we are open.");
}

/** A dish staff marked as run out today cannot be ordered from the app, whatever an old copy of the menu on a phone says. */
async function requireInStock(ctx: Ctx, menuItemIds: readonly string[]): Promise<void> {
  for (const id of new Set(menuItemIds)) {
    const item = await ctx.tx.get("menuItems", id);
    if (item?.soldOutDate === ctx.businessDate) fail("failed-precondition", `${item.name} is sold out today. Please remove it from your order.`);
  }
}

const toOrderInput = (input: z.infer<typeof OnlineOrderInputSchema>) => ({
  idempotencyKey: input.idempotencyKey, type: "takeaway", customerPhone: input.customerPhone, customerName: input.customerName,
  lines: input.lines, payments: [],
  // The note is what the kitchen ticket prints, so it says where the order came from.
  note: `${ONLINE_CHANNEL_LABELS[input.channel].toUpperCase()} - ${input.customerName}${input.note ? ` - ${input.note}` : ""}`
});

/** Exact price of a basket (menu prices and automatic promotions), without creating anything. */
export async function quoteOnlineOrder(ctx: Ctx, raw: unknown) {
  const input = parse(OnlineOrderInputSchema.omit({ idempotencyKey: true, customerName: true, customerPhone: true, channel: true }), raw);
  await warm(ctx, input.lines.map((line) => line.menuItemId));
  await requireInStock(ctx, input.lines.map((line) => line.menuItemId));
  const { totals, discounts } = await quoteOrder(ctx, toOrderInput({ ...input, idempotencyKey: newId(), customerName: "Customer", customerPhone: "0700000000", channel: "app" }), true);
  return { totals, discounts: discounts.map((discount) => ({ label: discount.label, amountMinor: discount.amountMinor })) };
}

export async function createOnlineOrder(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(OnlineOrderInputSchema, raw);
  const byPhone = [{ field: "customerPhone", op: "==" as const, value: input.customerPhone }];
  await warm(ctx, input.lines.map((line) => line.menuItemId), [ctx.tx.query("orders", byPhone), ctx.tx.query("customers", [{ field: "phone", op: "==", value: input.customerPhone }])], input.channel);
  await requireOpenForOrders(ctx);
  // Deliberately vague: someone placing fake orders learns nothing about why.
  if ((await ctx.tx.query("customers", [{ field: "phone", op: "==", value: input.customerPhone }])).some((customer) => customer.blockedFromApp)) fail("permission-denied", "We cannot take app orders from this phone number. Please call the restaurant.");
  await requireInStock(ctx, input.lines.map((line) => line.menuItemId));
  const waiting = (await ctx.tx.query("orders", byPhone)).filter((order) => order.source === "app" && order.status === "open" && order.paidMinor === 0);
  if (waiting.length >= MAX_OPEN_ONLINE_ORDERS) fail("failed-precondition", "You already have orders waiting to be collected. Please collect or pay for them first.");
  // Held: the kitchen hears nothing and no stock is used until staff have looked at the order and accepted it.
  const result = await placeOrder(ctx, toOrderInput(input), input.channel);
  const order = (await ctx.tx.get("orders", result.orderId))!;
  return { orderId: order.id, dailyNumber: order.dailyNumber, ref: order.ref, publicToken: order.publicToken, totalMinor: order.totals.totalMinor };
}

/**
 * Records a card payment confirmed by the payment gateway. The gateway may repeat its message, so
 * the gateway's own payment id makes this idempotent. Money that cannot be matched to an amount due
 * (the order was cancelled or already settled at the counter) is not booked; it is written to the
 * audit log so a manager can refund it at the gateway.
 */
export async function payOnlineOrder(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "payments.capture");
  const input = parse(z.object({ orderRef: z.string().min(6).max(40), amountMinor: z.number().int().positive(), gatewayPaymentId: z.string().min(1).max(60) }), raw);
  const order = (await ctx.tx.query("orders", [{ field: "ref", op: "==", value: input.orderRef }]))[0] as Order | undefined;
  if (!order || order.source !== "app") fail("not-found", "Order not found.");
  const reference = `PayHere ${input.gatewayPaymentId}`;
  if ((await ctx.tx.query("payments", [{ field: "orderId", op: "==", value: order.id }])).some((payment) => payment.reference === reference)) return { orderId: order.id, recorded: false, paymentState: order.paymentState };
  const dueMinor = order.status === "open" ? order.totals.totalMinor - order.paidMinor : 0;
  const amountMinor = Math.min(input.amountMinor, dueMinor);
  if (amountMinor < input.amountMinor) audit(ctx, "payment.online_unmatched", "order", order.id, { dailyNumber: order.dailyNumber, receivedMinor: input.amountMinor, bookedMinor: amountMinor, reference, status: order.status });
  if (amountMinor <= 0) return { orderId: order.id, recorded: false, paymentState: order.paymentState };
  const shift = (await ctx.tx.query("shifts", [{ field: "status", op: "==", value: "open" }]))[0];
  ctx.tx.set("payments", {
    id: newId(), orderId: order.id, dailyNumber: order.dailyNumber, method: "card", amountMinor, tenderedMinor: amountMinor, changeMinor: 0, reference, shiftId: shift?.id ?? NO_SHIFT,
    createdBy: ctx.actor.id, createdByName: ctx.actor.name, createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1
  });
  order.paidMinor += amountMinor;
  order.paymentState = paymentStateOf(order);
  if (order.paidMinor >= order.totals.totalMinor) await settle(ctx, order, await getSettings(ctx.tx));
  audit(ctx, "payment.captured", "order", order.id, { dailyNumber: order.dailyNumber, amountMinor, methods: ["card"], state: order.paymentState, online: true });
  ctx.tx.set("orders", { ...order, updatedAt: ctx.iso, version: order.version + 1 });
  return { orderId: order.id, recorded: true, paymentState: order.paymentState };
}

async function appOrder(ctx: Ctx, orderId: string, state: "pending" | "accepted"): Promise<Order> {
  const order = await ctx.tx.get("orders", orderId);
  if (!order || order.source !== "app") fail("not-found", "Order not found.");
  if (order.status !== "open") fail("failed-precondition", "This order is already closed.");
  if ((order.onlineState ?? "accepted") !== state) fail("failed-precondition", state === "pending" ? "This order was already accepted." : "Accept the order first.");
  return order;
}
const PrepMinutes = z.number().int().min(5).max(240);
const readyTime = (ctx: Ctx, minutes: number) => new Date(ctx.now.getTime() + minutes * 60_000).toISOString();

/**
 * Staff take on an app order: they say how long it will take and strike any dish that has run out.
 * Only now is the bill final, the stock used and the kitchen ticket created - so the kitchen never
 * starts on an order that staff have not seen, or on a dish that is not there.
 */
export async function acceptOnlineOrder(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({ orderId: z.string().min(1), prepMinutes: PrepMinutes, unavailableLineIds: z.array(z.string().max(128)).max(100).default([]), message: z.string().trim().max(200).default("") }), raw);
  const order = await appOrder(ctx, input.orderId, "pending");
  await warm(ctx, order.lines.map((line) => line.menuItemId), order.customerId ? [ctx.tx.get("customers", order.customerId)] : []);
  const settings = await getSettings(ctx.tx);
  const unavailable = order.lines.filter((line) => input.unavailableLineIds.includes(line.id));
  for (const line of unavailable) { line.voided = true; line.voidReason = "Not available"; }
  const lines = order.lines.filter((line) => !line.voided);
  if (lines.length === 0) fail("failed-precondition", "Nothing would be left on this order. Reject it instead.");
  if (unavailable.length > 0) await reprice(ctx, order, settings, false, undefined);
  const ticket = createTicket(ctx, order, lines, 1, settings);
  await moveStock(ctx, order, lines, -1);
  order.onlineState = "accepted"; order.acceptedAt = ctx.iso; order.readyAt = readyTime(ctx, input.prepMinutes); order.onlineMessage = input.message;
  audit(ctx, "online.order_accepted", "order", order.id, { dailyNumber: order.dailyNumber, prepMinutes: input.prepMinutes, unavailable: unavailable.map((line) => line.name), totalMinor: order.totals.totalMinor });
  ctx.tx.set("orders", { ...order, updatedAt: ctx.iso, version: order.version + 1 });
  return { orderId: order.id, ticketId: ticket.id, readyAt: order.readyAt, totalMinor: order.totals.totalMinor };
}

/** Staff turn an app order down before anything was cooked; the customer is shown the reason. */
export async function rejectOnlineOrder(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({ orderId: z.string().min(1), reason: z.string().trim().min(3, "Say why, so the customer knows").max(200) }), raw);
  const order = await appOrder(ctx, input.orderId, "pending");
  if (order.paidMinor > 0) fail("failed-precondition", "This order has a payment. Accept it, or refund and cancel it from the order screen.");
  audit(ctx, "order.voided", "order", order.id, { dailyNumber: order.dailyNumber, totalMinor: order.totals.totalMinor, online: true }, { reason: input.reason });
  ctx.tx.set("orders", { ...order, status: "voided", voidedAt: ctx.iso, voidReason: input.reason, onlineMessage: input.reason, updatedAt: ctx.iso, version: order.version + 1 });
  return { orderId: order.id };
}

/** Seconds the customer has left to cancel in the app (0 when it is too late, paid, or closed). */
export function customerCancelSecondsLeft(order: Order, now: Date): number {
  if (order.source !== "app" || order.status !== "open" || order.paidMinor > 0) return 0;
  return Math.max(0, Math.ceil((Date.parse(order.createdAt) + CUSTOMER_CANCEL_SECONDS * 1000 - now.getTime()) / 1000));
}

/**
 * The customer cancels in the app, within 30 seconds of placing the order. If staff already accepted it in that
 * time, what was sent to the kitchen is withdrawn and the ingredients go back into stock. A paid order is
 * never cancelled here: that needs a refund.
 */
export async function cancelOnlineOrder(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({ orderId: z.string().min(1) }), raw);
  const order = await ctx.tx.get("orders", input.orderId);
  if (!order || order.source !== "app") fail("not-found", "Order not found.");
  if (order.status !== "open") fail("failed-precondition", "This order is already closed.");
  if (order.paidMinor > 0) fail("failed-precondition", "This order is paid. Please call the restaurant to cancel it.");
  if (customerCancelSecondsLeft(order, ctx.now) === 0) fail("failed-precondition", "The time to cancel in the app has passed, and the kitchen has started on this order. Please call the restaurant.");
  if (order.onlineState !== "pending") {
    await moveStock(ctx, order, order.lines.filter((line) => !line.voided), 1);
    for (const ticket of await ctx.tx.query("kitchenTickets", [{ field: "orderId", op: "==", value: order.id }])) {
      if (ticket.status !== "served") ctx.tx.set("kitchenTickets", { ...ticket, status: "cancelled" });
    }
  }
  audit(ctx, "order.voided", "order", order.id, { dailyNumber: order.dailyNumber, totalMinor: order.totals.totalMinor, online: true, byCustomer: true, wasAccepted: order.onlineState !== "pending" }, { reason: CUSTOMER_CANCEL_REASON });
  ctx.tx.set("orders", { ...order, status: "voided", voidedAt: ctx.iso, voidReason: CUSTOMER_CANCEL_REASON, onlineMessage: "", updatedAt: ctx.iso, version: order.version + 1 });
  return { orderId: order.id };
}

/** The kitchen is running late (or early): tell the customer a new time. */
export async function setOnlineReadyTime(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({ orderId: z.string().min(1), prepMinutes: PrepMinutes, message: z.string().trim().max(200).optional() }), raw);
  const order = await appOrder(ctx, input.orderId, "accepted");
  const readyAt = readyTime(ctx, input.prepMinutes);
  ctx.tx.set("orders", { ...order, readyAt, ...(input.message === undefined ? {} : { onlineMessage: input.message }), updatedAt: ctx.iso, version: order.version + 1 });
  return { orderId: order.id, readyAt };
}

/** Staff tell the customer the order is waiting at the counter. Needed where no kitchen screen marks tickets ready. */
export async function markOnlineOrderReady(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({ orderId: z.string().min(1) }), raw);
  const order = await appOrder(ctx, input.orderId, "accepted");
  if (!order.collectReadyAt) ctx.tx.set("orders", { ...order, collectReadyAt: ctx.iso, updatedAt: ctx.iso, version: order.version + 1 });
  return { orderId: order.id };
}

/** Marks a dish as run out for today in the customer app (or puts it back). It returns by itself the next day. */
export async function setSoldOut(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "orders.create");
  const input = parse(z.object({ menuItemId: z.string().min(1).max(128), soldOut: z.boolean() }), raw);
  const item = await ctx.tx.get("menuItems", input.menuItemId) ?? fail("not-found", "Menu item not found.");
  ctx.tx.set("menuItems", { ...item, soldOutDate: input.soldOut ? ctx.businessDate : null, updatedAt: ctx.iso });
  const settings = await getSettings(ctx.tx);
  const today = settings.soldOutToday?.date === ctx.businessDate ? settings.soldOutToday.itemIds.filter((id) => id !== item.id) : [];
  ctx.tx.set("settings", { ...settings, soldOutToday: { date: ctx.businessDate, itemIds: input.soldOut ? [...today, item.id] : today } });
  audit(ctx, "menu.sold_out", "menuItem", item.id, { name: item.name, soldOut: input.soldOut });
  return { menuItemId: item.id, soldOut: input.soldOut };
}

/** Bars a phone number from ordering in the app, or lifts the bar. Counter and call orders are not affected. */
export async function blockPhone(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "shifts.manage");
  const input = parse(z.object({ phone: OnlineOrderInputSchema.shape.customerPhone, blocked: z.boolean(), reason: z.string().trim().max(200).default("") }), raw);
  const existing = (await ctx.tx.query("customers", [{ field: "phone", op: "==", value: input.phone }]))[0];
  const customer: Customer = existing ?? { id: newId(), phone: input.phone, name: "", birthday: "", notes: "", points: 0, totalSpentMinor: 0, orderCount: 0, lastOrderAt: null, marketingConsent: false, active: true, createdAt: ctx.iso, updatedAt: ctx.iso, schemaVersion: 1 };
  ctx.tx.set("customers", { ...customer, blockedFromApp: input.blocked, blockedReason: input.blocked ? input.reason : "", updatedAt: ctx.iso });
  audit(ctx, input.blocked ? "online.phone_blocked" : "online.phone_unblocked", "customer", customer.id, { phone: input.phone }, { reason: input.reason });
  return { customerId: customer.id, blocked: input.blocked };
}

/** What the ordering app tells customers about the restaurant, beyond the menu. */
export async function setOnlineInfo(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "shifts.manage");
  const { openingHours } = parse(z.object({ openingHours: z.string().trim().max(120) }), raw);
  ctx.tx.set("settings", { ...await getSettings(ctx.tx), openingHours, updatedAt: ctx.iso });
  return { openingHours };
}

export const MAX_APP_OFFERS = 5;
/**
 * Publishes an offer to customers who have the app: it shows at the top of the menu, and phones with
 * the Android app announce it as a notification within about an hour.
 */
export async function saveOffer(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "promotions.manage");
  const input = parse(z.object({
    title: z.string().trim().min(2, "Give the offer a short title").max(60), text: z.string().trim().max(240).default(""),
    until: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null), promotionId: z.string().max(128).nullable().default(null),
    // The discount itself, set up with the offer: percent (basis points) or rupees (cents) off whole order, categories or dishes.
    discount: z.object({
      mode: z.enum(["percent", "fixed"]), value: z.number().int().positive(),
      itemIds: z.array(z.string().max(128)).max(200).default([]), categoryIds: z.array(z.string().max(128)).max(50).default([])
    }).nullable().default(null),
    audience: z.enum(["app", "dine_in", "all"]).default("app")
  }), raw);
  if (input.until && input.until < ctx.businessDate) fail("invalid-argument", "The end date has already passed.");
  let ownsPromotion = false;
  if (input.discount) {
    const { mode, value, itemIds, categoryIds } = input.discount;
    if (mode === "percent" && value > 10_000) fail("invalid-argument", "A discount cannot be more than 100%.");
    // Dishes or categories chosen: the discount is on those lines. Nothing chosen: it comes off the whole order.
    const scope = itemIds.length > 0 || categoryIds.length > 0 ? "item" : "bill";
    const promotion: Promotion = {
      id: newId(), name: input.title, kind: mode === "percent" ? "percentage" : "fixed", scope, valueBps: mode === "percent" ? value : 0, valueMinor: mode === "fixed" ? value : 0,
      itemIds, categoryIds, minSpendMinor: 0, maxDiscountMinor: 0, startDate: ctx.businessDate, endDate: input.until, days: [], fromMinutes: 0, toMinutes: 0,
      // An app offer discounts app orders only, a dine-in offer dine-in bills only; "all" is for everyone.
      channels: input.audience === "app" ? ["app"] : input.audience === "dine_in" ? ["dine_in"] : [],
      buyQty: 0, getQty: 0, autoApply: true, stackable: true, active: true, createdAt: ctx.iso, updatedAt: ctx.iso, schemaVersion: 1
    };
    ctx.tx.set("promotions", promotion);
    audit(ctx, "promotion.created", "promotion", promotion.id, { name: promotion.name, kind: promotion.kind, scope, value, fromOffer: true });
    input.promotionId = promotion.id; ownsPromotion = true;
  } else if (input.promotionId) {
    // The discount must really be taken off app orders, which only happens for promotions that apply by themselves.
    const promotion = await ctx.tx.get("promotions", input.promotionId) ?? fail("not-found", "That promotion no longer exists.");
    if (!promotion.active || !promotion.autoApply) fail("failed-precondition", `Turn on "active" and "apply automatically" for ${promotion.name} first, so app and counter orders get the discount.`);
    if (!input.until && promotion.endDate) input.until = promotion.endDate;
  }
  const settings = await getSettings(ctx.tx);
  const live = (settings.appOffers ?? []).filter((offer) => !offer.until || offer.until >= ctx.businessDate);
  if (live.length >= MAX_APP_OFFERS) fail("failed-precondition", `At most ${MAX_APP_OFFERS} offers can run at once. Remove one first.`);
  if (!input.text) {
    const discount = input.discount;
    input.text = discount ? `${discount.mode === "percent" ? `${discount.value / 100}%` : `Rs ${discount.value / 100}`} off${discount.itemIds.length > 0 || discount.categoryIds.length > 0 ? "" : " your order"}` : input.title;
  }
  const { discount: _discount, ...fields } = input;
  const offer = { id: newId(), ...fields, ownsPromotion, createdAt: ctx.iso };
  ctx.tx.set("settings", { ...settings, appOffers: [...live, offer], updatedAt: ctx.iso });
  audit(ctx, "online.offer_published", "settings", "restaurant", { title: offer.title, until: offer.until });
  return { offerId: offer.id };
}
export async function removeOffer(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "promotions.manage");
  const { offerId } = parse(z.object({ offerId: z.string().min(1).max(64) }), raw);
  const settings = await getSettings(ctx.tx);
  const removed = (settings.appOffers ?? []).find((offer) => offer.id === offerId);
  ctx.tx.set("settings", { ...settings, appOffers: (settings.appOffers ?? []).filter((offer) => offer.id !== offerId), updatedAt: ctx.iso });
  // A discount made for this offer ends with it, at the counter too.
  if (removed?.ownsPromotion && removed.promotionId) {
    const promotion = await ctx.tx.get("promotions", removed.promotionId);
    if (promotion) ctx.tx.set("promotions", { ...promotion, active: false, updatedAt: ctx.iso });
  }
  audit(ctx, "online.offer_removed", "settings", "restaurant", { offerId });
  return { offerId };
}

/** Pauses or resumes ordering from the app, for a kitchen that cannot take more. Whoever runs the shift decides. */
export async function setOnlineOrdering(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "shifts.manage");
  const { enabled } = parse(z.object({ enabled: z.boolean() }), raw);
  ctx.tx.set("settings", { ...await getSettings(ctx.tx), onlineOrdersEnabled: enabled, updatedAt: ctx.iso });
  audit(ctx, "online.ordering_switched", "settings", "restaurant", { enabled });
  return { enabled };
}
